// ============================================================================
// lib/remote/contractHost.ts — БРАУЗЕРНЫЙ хост НАШЕГО wire-контракта v2.
//
// Браузер (Chrome/PWA/WebView2) захватывает экран (getDisplayMedia), кодирует
// H.264 (WebCodecs VideoEncoder, Annex-B) и режет кадр на видео-пакеты
// (lib/remote/contractVideo — тот же формат, что у Android-хоста и нативного
// движка), управление — текстовые кадры JSON `{t:…}`.
//
// Основной режим — ЧЕРЕЗ РЕЛЕЙ сервера (role=host): соединение ИСХОДЯЩЕЕ,
// входящие порты не нужны, NAT не мешает (см. docs/platform/remote-access.md
// «Релей (WAN)», plans/remote-protocol-contract.md «Релей»).
//
// Управление вводом браузерный хост НЕ выполняет (в браузере нет нативной
// инъекции) — запрос управления получает честный отказ `{t:"control",on:false}`.
// ============================================================================

import { encodeVideoPackets } from '@/lib/remote/contractVideo';
import { captureScreenStream, remotePreset } from '@/lib/remote/remoteSession';

export type BrowserHostPhase =
  | 'idle'
  | 'capturing'
  | 'connecting'
  | 'waiting'
  | 'paired'
  | 'streaming'
  | 'stopped'
  | 'error';

export interface BrowserHostStats {
  fps: number;
  kbps: number;
}

export interface BrowserContractHostOptions {
  /** Адрес подключения: релей сервера (role=host&token=…) или прямой ws(s). */
  url: string;
  sessionId: string;
  /** Пресет качества (auto|high|medium|low) — из REMOTE_QUALITY_PRESETS. */
  quality?: string;
  onPhase: (phase: BrowserHostPhase, message?: string) => void;
  onError: (message: string) => void;
  onNotice?: (message: string) => void;
  onStats?: (stats: BrowserHostStats) => void;
}

/** Поддержка браузерного хоста в текущем окружении (явная проверка). */
export function isBrowserContractHostSupported(): boolean {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') return false;
  return !!navigator.mediaDevices
    && typeof navigator.mediaDevices.getDisplayMedia === 'function'
    && typeof VideoEncoder !== 'undefined'
    && typeof VideoFrame !== 'undefined'
    && typeof WebSocket !== 'undefined';
}

const KEYFRAME_INTERVAL_MS = 2000;
const MAX_ENCODE_QUEUE = 2;

type VideoWithRvfc = HTMLVideoElement & {
  requestVideoFrameCallback?: (cb: (now: number, meta: unknown) => void) => number;
  cancelVideoFrameCallback?: (handle: number) => void;
};

interface HostMessage {
  t?: string;
  id?: number;
  event?: string;
  message?: string;
}

export class BrowserContractHost {
  private readonly opts: BrowserContractHostOptions;
  private stream: MediaStream | null = null;
  private video: VideoWithRvfc | null = null;
  private encoder: VideoEncoder | null = null;
  private ws: WebSocket | null = null;
  private rvfcHandle = 0;
  private interval = 0;
  private statsTimer = 0;
  private running = false;
  private seq = 0;
  private forceKeyframe = false;
  private lastKeyMs = 0;
  private framesWindow = 0;
  private bytesWindow = 0;
  private streaming = false;

  constructor(opts: BrowserContractHostOptions) {
    this.opts = opts;
  }

  get isRunning(): boolean {
    return this.running;
  }

  /** Старт: захват экрана (нужен жест пользователя) → кодер → WS к релею. */
  async start(): Promise<void> {
    if (this.running) return;
    if (!isBrowserContractHostSupported()) {
      throw new Error('Показ экрана из браузера недоступен: нет захвата экрана или кодека H.264 (WebCodecs)');
    }
    this.running = true;
    this.seq = 0;
    this.streaming = false;
    this.opts.onPhase('capturing');
    let stream: MediaStream;
    try {
      stream = await captureScreenStream(remotePreset(this.opts.quality || 'auto'));
    } catch (e) {
      this.running = false;
      const msg = `Не удалось получить доступ к экрану: ${String((e as Error)?.message || e)}`;
      this.opts.onPhase('error', msg);
      throw new Error(msg);
    }
    this.stream = stream;
    const track = stream.getVideoTracks()[0];
    track?.addEventListener('ended', () => {
      if (!this.running) return;
      this.opts.onNotice?.('Показ экрана остановлен из системного интерфейса');
      this.stop();
    });

    const video = document.createElement('video') as VideoWithRvfc;
    video.muted = true;
    video.playsInline = true;
    video.srcObject = stream;
    this.video = video;
    try {
      await new Promise<void>((resolve, reject) => {
        video.onloadedmetadata = () => resolve();
        video.onerror = () => reject(new Error('поток экрана не открылся'));
      });
      await video.play();
      this.startEncoder(track);
    } catch (e) {
      this.fail(`Подготовка кодера: ${String((e as Error)?.message || e)}`);
      throw e;
    }

    this.opts.onPhase('connecting');
    let ws: WebSocket;
    try {
      ws = new WebSocket(this.opts.url);
    } catch (e) {
      this.fail(`Не удалось открыть соединение с релеем: ${String((e as Error)?.message || e)}`);
      return;
    }
    ws.binaryType = 'arraybuffer';
    this.ws = ws;
    ws.onopen = () => {
      if (!this.running) return;
      // Релей пришлёт waiting/paired; прямой режим — сразу показываем.
      this.opts.onPhase(this.isRelay() ? 'waiting' : 'paired');
      this.startCaptureLoop();
    };
    ws.onmessage = (ev: MessageEvent<ArrayBuffer | string>) => {
      if (typeof ev.data === 'string') this.onText(ev.data);
    };
    ws.onerror = () => {
      if (!this.running) return;
      this.fail('Ошибка соединения с релеем (сервер недоступен или соединение сброшено)');
    };
    ws.onclose = () => {
      if (!this.running) return;
      this.opts.onNotice?.('Соединение с релеем закрыто');
      this.stop();
    };
    this.statsTimer = window.setInterval(() => this.tickStats(), 1000);
  }

  /** Остановка: кодер, WebSocket, захват экрана (идемпотентно). */
  stop(): void {
    const wasRunning = this.running;
    this.teardown();
    if (wasRunning) this.opts.onPhase('stopped');
  }

  /** Освобождение ресурсов (без событий фазы — их шлёт вызывающий). */
  private teardown(): void {
    this.running = false;
    if (this.rvfcHandle && this.video?.cancelVideoFrameCallback) {
      this.video.cancelVideoFrameCallback(this.rvfcHandle);
    }
    this.rvfcHandle = 0;
    if (this.interval) {
      window.clearInterval(this.interval);
      this.interval = 0;
    }
    if (this.statsTimer) {
      window.clearInterval(this.statsTimer);
      this.statsTimer = 0;
    }
    try {
      if (this.encoder && this.encoder.state !== 'closed') this.encoder.close();
    } catch {
      /* кодер уже закрыт */
    }
    this.encoder = null;
    const ws = this.ws;
    this.ws = null;
    if (ws) {
      try {
        ws.onopen = null;
        ws.onmessage = null;
        ws.onerror = null;
        ws.onclose = null;
        if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) ws.close(1000, 'host stopped');
      } catch {
        /* соединение уже закрыто */
      }
    }
    if (this.stream) {
      for (const t of this.stream.getTracks()) t.stop();
      this.stream = null;
    }
    if (this.video) {
      this.video.srcObject = null;
      this.video = null;
    }
  }

  private isRelay(): boolean {
    return this.opts.url.includes('/remote/relay/');
  }

  private startEncoder(track: MediaStreamTrack | undefined): void {
    const preset = remotePreset(this.opts.quality || 'auto');
    const settings = (track?.getSettings?.() || {}) as MediaTrackSettings;
    const width = settings.width || this.video?.videoWidth || preset.width;
    const height = settings.height || this.video?.videoHeight || preset.height;
    this.encoder = new VideoEncoder({
      output: (chunk) => this.onEncoded(chunk),
      error: (e) => this.fail(`Кодер H.264: ${e.message}`),
    });
    const config = {
      codec: 'avc1.42E01F', // Baseline 3.1 — совместим со всеми зрителями (Android/WebCodecs)
      width: width || preset.width,
      height: height || preset.height,
      bitrate: preset.maxBitrate,
      framerate: preset.fps,
      latencyMode: 'realtime',
      hardwareAcceleration: 'prefer-hardware',
      avc: { format: 'annexb' }, // SPS/PPS — в кейфрейме (in-band), как у движка
    } as VideoEncoderConfig & { avc: { format: string } };
    try {
      this.encoder.configure(config);
    } catch (e) {
      this.fail(`Не удалось настроить кодер H.264: ${String((e as Error)?.message || e)}`);
    }
  }

  private startCaptureLoop(): void {
    const preset = remotePreset(this.opts.quality || 'auto');
    const video = this.video;
    if (!video) return;
    if (typeof video.requestVideoFrameCallback === 'function') {
      const step = () => {
        if (!this.running) return;
        this.encodeFrame();
        this.rvfcHandle = video.requestVideoFrameCallback?.(step) || 0;
      };
      this.rvfcHandle = video.requestVideoFrameCallback(step);
      return;
    }
    this.interval = window.setInterval(() => this.encodeFrame(), Math.max(1, Math.round(1000 / preset.fps)));
  }

  private encodeFrame(): void {
    const enc = this.encoder;
    const video = this.video;
    if (!enc || enc.state !== 'configured' || !video) return;
    if (enc.encodeQueueSize > MAX_ENCODE_QUEUE) return; // не копим задержку — пропускаем кадр
    let frame: VideoFrame;
    try {
      frame = new VideoFrame(video, { timestamp: Math.round(performance.now() * 1000) });
    } catch (e) {
      this.fail(`Кадр не создан: ${String((e as Error)?.message || e)}`);
      return;
    }
    try {
      const key = this.forceKeyframe || performance.now() - this.lastKeyMs > KEYFRAME_INTERVAL_MS;
      if (key) this.lastKeyMs = performance.now();
      this.forceKeyframe = false;
      enc.encode(frame, { keyFrame: key });
    } catch (e) {
      this.opts.onNotice?.(`Кадр не принят кодером: ${String((e as Error)?.message || e)}`);
    } finally {
      frame.close();
    }
  }

  private onEncoded(chunk: EncodedVideoChunk): void {
    const ws = this.ws;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    const buf = new Uint8Array(chunk.byteLength);
    chunk.copyTo(buf);
    const keyframe = chunk.type === 'key';
    const ptsMs = (chunk.timestamp || 0) / 1000;
    const packets = encodeVideoPackets(buf, this.seq++, ptsMs, keyframe, true);
    let bytes = 0;
    try {
      for (const p of packets) {
        ws.send(p);
        bytes += p.byteLength;
      }
    } catch {
      return; // соединение закрылось между проверкой и отправкой
    }
    this.framesWindow += 1;
    this.bytesWindow += bytes;
    if (!this.streaming && keyframe) {
      this.streaming = true;
      this.opts.onPhase('streaming');
    }
  }

  private onText(text: string): void {
    let msg: HostMessage;
    try {
      msg = JSON.parse(text) as HostMessage;
    } catch {
      return;
    }
    switch (msg.t) {
      case 'ping':
        this.send({ t: 'pong', id: msg.id ?? 0 });
        return;
      case 'hello':
        this.forceKeyframe = true; // новый зритель — внеочередной IDR
        return;
      case 'control_request':
        // Браузерный хост не выполняет ввод — честный отказ (без фоллбэков).
        this.send({ t: 'control', on: false });
        this.opts.onNotice?.('Запрошено управление, но браузерный показ экрана управление не поддерживает');
        return;
      case 'relay': {
        const event = msg.event || '';
        if (event === 'waiting') {
          this.opts.onPhase('waiting');
        } else if (event === 'paired') {
          this.forceKeyframe = true; // зритель видит чистое начало
          this.opts.onPhase('paired');
        } else if (event === 'peer_gone') {
          this.streaming = false;
          this.opts.onPhase('waiting', 'Зритель отключился — ждём новое подключение');
        } else if (event === 'timeout') {
          this.fail(msg.message || 'Вторая сторона не подключилась к релею');
        } else if (event === 'role_taken') {
          this.fail('Сеанс релея уже занят (эта роль подключена из другого окна)');
        }
        return;
      }
      default:
        return;
    }
  }

  private send(obj: Record<string, unknown>): void {
    const ws = this.ws;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    try {
      ws.send(JSON.stringify(obj));
    } catch {
      /* соединение закрылось */
    }
  }

  private tickStats(): void {
    const fps = this.framesWindow;
    const kbps = Math.round((this.bytesWindow * 8) / 1000);
    this.framesWindow = 0;
    this.bytesWindow = 0;
    this.opts.onStats?.({ fps, kbps });
  }

  private fail(message: string): void {
    this.teardown();
    this.opts.onPhase('error', message);
    this.opts.onError(message);
  }
}
